// src/components/GiftCardPurchase.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Gift, Check, Sparkles } from "lucide-react";
import { NavbarDemo } from "./NavBar";

export default function GiftCardPurchase() {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [amountError, setAmountError] = useState("");

  const giftCardOptions = [
    {
      id: "basic",
      amount: 50,
      label: "$50 Basic",
      description: "Perfect starter gift",
      tier: "Basic",
    },
    {
      id: "standard",
      amount: 100,
      label: "$100 Standard",
      description: "Full relaxation experience",
      popular: true,
      tier: "Standard",
      promoEligible: true, // Eligible for 15% off
    },
    {
      id: "premium",
      amount: 200,
      label: "$200 Premium",
      description: "Ultimate luxury package",
      tier: "Premium",
      promoEligible: true, // Eligible for 15% off
    },
  ];

  const roundToTwoDecimals = (value) => {
    return Math.round(value * 100) / 100;
  };

  const handleSelectOption = (option) => {
    setSelectedOption(option);
    setCustomAmount("");
    setAmountError("");
  };

  const handleCustomAmount = (value) => {
    // Validate and restrict decimal places to 2
    if (value && value.includes('.')) {
      const parts = value.split('.');
      if (parts[1] && parts[1].length > 2) {
        // Limit to 2 decimal places
        value = parts[0] + '.' + parts[1].substring(0, 2);
      }
    }

    setCustomAmount(value);
    setAmountError("");

    if (!value || value.trim() === "") {
      setSelectedOption(null);
      return;
    }

    const amount = parseFloat(value);
    if (isNaN(amount)) {
      setSelectedOption(null);
      return;
    }

    if (amount < 10 || amount > 500) {
      setSelectedOption(null);
      return;
    }

    // Round to 2 decimal places
    const roundedAmount = roundToTwoDecimals(amount);
    setSelectedOption({
      id: "custom",
      amount: roundedAmount,
      label: `$${roundedAmount.toFixed(2)} Custom Gift Card`,
      description: "Custom amount",
    });
  };

  const handleContinue = () => {
    if (!selectedOption) return;

    // Validate custom amount if selected
    if (selectedOption.id === "custom" && customAmount) {
      const amount = parseFloat(customAmount);
      if (isNaN(amount) || amount < 10) {
        setAmountError("Please enter a minimum amount of $10.00");
        return;
      }
      if (amount > 500) {
        setAmountError("Please enter a maximum amount of $500.00");
        return;
      }

      // Round and update the selected option
      const roundedAmount = roundToTwoDecimals(amount);
      const updatedOption = {
        ...selectedOption,
        amount: roundedAmount,
        label: `$${roundedAmount.toFixed(2)} Custom Gift Card`,
      };

      navigate("/gift-cards/customize", {
        state: { giftCard: updatedOption },
      });
    } else {
      navigate("/gift-cards/customize", {
        state: { giftCard: selectedOption },
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDEFF7] to-white">
      {/* Header */}
      <NavbarDemo />
      <div className="bg-white border-b border-[#D3D6E0]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#252F4F] to-[#40424D] flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[#252F4F]">
                Gift the Perfect Massage
              </h1>
              <p className="text-[#6E7180] mt-1">
                Choose an amount and customize your gift card
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {giftCardOptions.map((option) => (
            <Card
              key={option.id}
              onClick={() => handleSelectOption(option)}
              className={`cursor-pointer transition-all duration-200 hover:shadow-xl ${
                selectedOption?.id === option.id
                  ? "ring-2 ring-[#98C454] shadow-lg border-[#98C454]"
                  : "hover:border-[#98C454]"
              }`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-xl text-[#252F4F]">
                    {option.label}
                  </CardTitle>
                  {option.popular && (
                    <Badge className="bg-[#98C454] text-white">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Popular
                    </Badge>
                  )}
                </div>
                <p className="text-[#6E7180] text-sm mt-2">
                  {option.description}
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-[#98C454]">
                    ${option.amount.toFixed(2)}
                  </span>
                  {selectedOption?.id === option.id && (
                    <Check className="w-6 h-6 text-[#98C454]" />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Custom Amount */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-[#252F4F]">
              Or Enter Custom Amount
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div>
                <label className="block text-sm font-medium text-[#252F4F] mb-2">
                  Custom Amount (minimum $10.00, maximum $500.00)
                </label>
                <Input
                  type="number"
                  min="10"
                  max="500"
                  step="0.01"
                  placeholder="Enter amount (e.g., 50.00)"
                  value={customAmount}
                  onChange={(e) => handleCustomAmount(e.target.value)}
                  className={`text-lg ${
                    amountError
                      ? "border-red-500 focus-visible:ring-red-500"
                      : ""
                  }`}
                />
                {amountError && (
                  <p className="text-sm text-red-500 mt-1">{amountError}</p>
                )}
                {selectedOption?.id === "custom" && !amountError && (
                  <p className="text-sm text-[#6E7180] mt-1">
                    Amount will be processed as $
                    {selectedOption.amount.toFixed(2)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Continue Button */}
        <div className="flex justify-end">
          <Button
            size="lg"
            onClick={handleContinue}
            disabled={!selectedOption}
            className="bg-[#98C454] hover:bg-[#7FA84A] text-white">
            Continue to Customize
          </Button>
        </div>
      </div>
    </div>
  );
}
